/**
 * MatchmakingInputV1 — frozen snapshot contract (schema validation only).
 */

export const V1_SCHEMA_VERSION = 1;

/** Top-level keys allowed on engine input after strict validation. */
export const V1_ALLOWED_TOP_KEYS = new Set([
  'schema_version',
  'session',
  'state',
  'execution',
]);

/** Keys allowed under state. */
export const V1_ALLOWED_STATE_KEYS = new Set([
  'session_players',
  'session_courts',
  'players',
  'partnerships',
  'scheduled_games',
  'match_history',
  'player_wait_metrics',
  'player_interactions',
  'excluded_player_ids',
  'total_games_completed',
  'pending_finish_game',
]);

const V1_ALLOWED_SESSION_KEYS = new Set([
  'id',
  'game_type_restriction',
  'competitive_match_format',
  'upcoming_queue_slot_count',
  'match_preferences_enabled',
  'cooldown_enabled',
  'relaxation_preset',
]);

/**
 * @param {unknown} value
 * @param {Set<string>} allowed
 * @param {string} path
 * @param {string[]} errors
 */
function rejectUnknownKeys(value, allowed, path, errors) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return;
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) {
      errors.push(`Unknown key ${path}.${key}`);
    }
  }
}

/**
 * @param {unknown} input
 * @param {{ strict?: boolean }} [options]
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateMatchmakingInputV1(input, { strict = false } = {}) {
  const errors = [];

  if (!input || typeof input !== 'object') {
    return { ok: false, errors: ['Input must be an object'] };
  }

  if (input.schema_version !== V1_SCHEMA_VERSION) {
    errors.push(`schema_version must be ${V1_SCHEMA_VERSION}`);
  }

  if (!input.session || typeof input.session !== 'object') {
    errors.push('session is required');
  }

  if (!input.state || typeof input.state !== 'object') {
    errors.push('state is required');
  } else {
    if (!Array.isArray(input.state.session_players)) {
      errors.push('state.session_players must be an array');
    }
    if (!Array.isArray(input.state.session_courts)) {
      errors.push('state.session_courts must be an array');
    }
    if (!input.state.players || typeof input.state.players !== 'object') {
      errors.push('state.players must be an object');
    }
  }

  if (!input.execution || typeof input.execution !== 'object') {
    errors.push('execution is required');
  } else if (!Array.isArray(input.execution.targets) || input.execution.targets.length === 0) {
    errors.push('execution.targets must be a non-empty array');
  } else {
    input.execution.targets.forEach((t, i) => {
      if (!t || typeof t !== 'object') {
        errors.push(`execution.targets[${i}] must be an object`);
        return;
      }
      if (t.court_type !== 'next_up' && t.court_type !== 'active') {
        errors.push(`execution.targets[${i}].court_type must be next_up or active`);
      }
      if (typeof t.index !== 'number' || Number.isNaN(t.index)) {
        errors.push(`execution.targets[${i}].index must be a number`);
      }
    });
  }

  if (strict) {
    rejectUnknownKeys(input, V1_ALLOWED_TOP_KEYS, 'root', errors);
    rejectUnknownKeys(input.session, V1_ALLOWED_SESSION_KEYS, 'session', errors);
    rejectUnknownKeys(input.state, V1_ALLOWED_STATE_KEYS, 'state', errors);
    if (input.execution) {
      rejectUnknownKeys(input.execution, new Set(['targets']), 'execution', errors);
    }
  }

  return { ok: errors.length === 0, errors };
}

/**
 * Strip debug_* fields from materialise RPC payload before engine (not part of V1).
 * @param {object} raw
 * @returns {object}
 */
export function extractV1FromMaterialiseResult(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const { matchmaking_input: input } = raw;
  if (!input) return input;
  return input;
}

/**
 * Migrate legacy materialiser / test fixture shape → V1.
 * @param {object} input
 * @returns {object}
 */
export function migrateMatchmakingInput(input) {
  if (!input || typeof input !== 'object') {
    throw new Error('migrateMatchmakingInput: input must be an object');
  }

  if (input.schema_version === V1_SCHEMA_VERSION && input.state?.session_players) {
    return input;
  }

  const postFinish = input.post_finish || {};
  const session = input.session || {};
  const playersById = postFinish.players_by_id || {};
  const sessionPlayers = synthesizeSessionPlayersFromLegacy(postFinish, playersById);
  const sessionCourts = synthesizeSessionCourtsFromLegacy(postFinish, session);

  const targets = deriveTargetsFromLegacy(input);

  return {
    schema_version: V1_SCHEMA_VERSION,
    session: {
      id: session.id ?? null,
      game_type_restriction: session.game_type_restriction ?? 'all_types',
      competitive_match_format: session.competitive_match_format ?? 'doubles',
      upcoming_queue_slot_count: session.upcoming_queue_slot_count ?? 1,
      match_preferences_enabled: session.match_preferences_enabled !== false,
      cooldown_enabled: session.cooldown_enabled === true,
      relaxation_preset: session.relaxation_preset ?? 'default',
    },
    state: {
      session_players: sessionPlayers,
      session_courts: sessionCourts,
      players: playersById,
      partnerships: postFinish.partnerships ?? [],
      scheduled_games: postFinish.scheduled_games ?? [],
      match_history: postFinish.match_history ?? [],
      player_wait_metrics: postFinish.player_wait_metrics ?? {},
      player_interactions: postFinish.player_interactions ?? {},
      excluded_player_ids: postFinish.excluded_player_ids ?? [],
      total_games_completed: postFinish.total_games_completed ?? 0,
      pending_finish_game: postFinish.pending_finish_game ?? null,
    },
    execution: { targets },
  };
}

/**
 * @param {object} postFinish
 * @param {Record<string, object>} playersById
 */
function synthesizeSessionPlayersFromLegacy(postFinish, playersById) {
  const rows = [];
  const seen = new Set();

  const pushRow = (playerId, fields) => {
    if (!playerId || seen.has(playerId)) return;
    seen.add(playerId);
    const p = playersById[playerId] || {};
    rows.push({
      player_id: playerId,
      player_list_status: p.player_list_status ?? fields.player_list_status ?? 'waitlist',
      court_type: fields.court_type ?? p.court_type ?? null,
      court_number: fields.court_number ?? p.court_number ?? null,
      team_number: fields.team_number ?? p.team_number ?? null,
      waitlist_position: p.waitlist_position ?? fields.waitlist_position ?? null,
      status: 'active',
    });
  };

  (postFinish.waitlist || []).forEach((id, idx) => {
    pushRow(id, { player_list_status: 'waitlist', waitlist_position: idx });
  });

  (postFinish.active_courts || []).forEach((court) => {
    const courtNumber = court.courtNumber;
    (court.team1 || []).forEach((id) => {
      pushRow(id, { court_type: 'active', court_number: courtNumber, team_number: 1, player_list_status: 'on_court' });
    });
    (court.team2 || []).forEach((id) => {
      pushRow(id, { court_type: 'active', court_number: courtNumber, team_number: 2, player_list_status: 'on_court' });
    });
  });

  (postFinish.next_up_slots || []).forEach((slot) => {
    const slotIndex = slot.slotIndex;
    (slot.team1 || []).forEach((id) => {
      pushRow(id, { court_type: 'next_up', court_number: slotIndex, team_number: 1, player_list_status: 'next_up' });
    });
    (slot.team2 || []).forEach((id) => {
      pushRow(id, { court_type: 'next_up', court_number: slotIndex, team_number: 2, player_list_status: 'next_up' });
    });
  });

  Object.keys(playersById).forEach((id) => {
    if (!seen.has(id)) {
      pushRow(id, {});
    }
  });

  return rows;
}

function synthesizeSessionCourtsFromLegacy(postFinish, session) {
  const courts = [];
  const slotCap = session.upcoming_queue_slot_count ?? 1;

  (postFinish.active_courts || []).forEach((c) => {
    courts.push({ court_type: 'active', court_number: c.courtNumber });
  });

  const nextSlots = postFinish.next_up_slots || [];
  for (let i = 0; i < slotCap; i += 1) {
    if (!courts.some((c) => c.court_type === 'next_up' && c.court_number === i)) {
      courts.push({ court_type: 'next_up', court_number: i });
    }
  }
  nextSlots.forEach((s) => {
    if (!courts.some((c) => c.court_type === 'next_up' && c.court_number === s.slotIndex)) {
      courts.push({ court_type: 'next_up', court_number: s.slotIndex });
    }
  });

  return courts;
}

function deriveTargetsFromLegacy(input) {
  const fillTarget = input.fill_target;
  if (fillTarget?.kind === 'active') {
    return [{ court_type: 'active', index: Number(fillTarget.courtNumber) }];
  }

  const slots = Array.isArray(input.slots_to_fill)
    ? input.slots_to_fill
    : input.post_finish?.slots_to_fill;

  if (Array.isArray(slots) && slots.length > 0) {
    return slots.map((idx) => ({ court_type: 'next_up', index: Number(idx) }));
  }

  return [{ court_type: 'next_up', index: 0 }];
}

/** @deprecated use V1_ALLOWED_TOP_KEYS */
export const V1_ALLOWED_KEYS = V1_ALLOWED_TOP_KEYS;
